import { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { Header } from './Header';
import { BottomNavigation } from './BottomNavigation';
import { SideNavigation } from './SideNavigation';
import { OfflineBanner } from './OfflineBanner';
import { ImpersonationBanner } from './ImpersonationBanner';
import { useAuthStore } from '@/stores/authStore';
import { useAccessRequestStore } from '@/stores/accessRequestStore';
import { useMessageStore } from '@/stores/messageStore';
import { useNotificationStore } from '@/stores/notificationStore';
import { syncAppBadgeCount } from '@/lib/messaging';

export function AppLayout() {
  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    return window.matchMedia('(min-width: 768px)').matches;
  });
  const user = useAuthStore((state) => state.user);
  const userData = useAuthStore((state) => state.userData);
  const subscribeToConversations = useMessageStore((state) => state.subscribeToConversations);
  const unsubscribeFromConversations = useMessageStore((state) => state.unsubscribeFromConversations);
  const accessRequests = useAccessRequestStore((state) => state.requests);
  const fetchReceivedRequests = useAccessRequestStore((state) => state.fetchReceivedRequests);
  const resetAccessRequests = useAccessRequestStore((state) => state.reset);
  const subscribeToNotifications = useNotificationStore((state) => state.subscribeToNotifications);
  const unsubscribeFromNotifications = useNotificationStore((state) => state.unsubscribeFromNotifications);
  const unreadVerificationResultCount = useNotificationStore(
    (state) => state.unreadVerificationResultCount
  );
  const unreadTotal = useMessageStore((state) =>
    state.conversations.reduce((sum, conversation) => sum + Math.max(0, conversation.unreadCount), 0)
  );
  const pendingAccessRequestCount =
    userData?.role === 'donor'
      ? accessRequests.filter((request) => request.status === 'pending').length
      : 0;

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const mediaQuery = window.matchMedia('(min-width: 768px)');
    const handleChange = (event: MediaQueryListEvent) => {
      setIsDesktop(event.matches);
    };

    setIsDesktop(mediaQuery.matches);
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  useEffect(() => {
    if (!user?.uid) {
      unsubscribeFromConversations();
      unsubscribeFromNotifications();
      return;
    }

    subscribeToConversations(user.uid);
    subscribeToNotifications(user.uid);

    return () => {
      unsubscribeFromConversations();
      unsubscribeFromNotifications();
    };
  }, [
    user?.uid,
    subscribeToConversations,
    unsubscribeFromConversations,
    subscribeToNotifications,
    unsubscribeFromNotifications,
  ]);

  useEffect(() => {
    if (!user?.uid || userData?.role !== 'donor') {
      resetAccessRequests();
      return;
    }

    void fetchReceivedRequests(user.uid);
    const timerId = window.setInterval(() => {
      void fetchReceivedRequests(user.uid);
    }, 60000);

    return () => {
      window.clearInterval(timerId);
    };
  }, [user?.uid, userData?.role, fetchReceivedRequests, resetAccessRequests]);

  useEffect(() => {
    if (!user?.uid) {
      void syncAppBadgeCount(0);
      return;
    }

    void syncAppBadgeCount(unreadTotal + unreadVerificationResultCount + pendingAccessRequestCount);
  }, [user?.uid, unreadTotal, unreadVerificationResultCount, pendingAccessRequestCount]);

  return (
    isDesktop ? (
      <div className="min-h-screen bg-[#E0DBD2] md:flex">
        <SideNavigation />

        <div className="flex min-h-screen min-w-0 flex-1 flex-col pt-[calc(4rem+env(safe-area-inset-top,0px))]">
          <Header />
          <OfflineBanner />
          <ImpersonationBanner />

          <main className="flex-1 min-w-0">
            <Outlet />
          </main>
        </div>
      </div>
    ) : (
      <div className="h-[100dvh] overflow-hidden bg-[#E0DBD2]">
        <div className="mx-auto flex h-full w-full max-w-[430px] flex-col bg-[#F7F4EF]">
          <OfflineBanner />
          <ImpersonationBanner />
          <main className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
            <Outlet />
          </main>

          <div className="flex-shrink-0">
            <BottomNavigation />
          </div>
        </div>
      </div>
    )
  );
}
